import { TFunction } from 'i18next';

export type View = 'day' | 'week' | 'month' | 'quarter' | 'all';

export const toDate = (d: string | Date) => (d instanceof Date ? d : new Date(d));

export const normalizeDate = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export function startOfPeriod(date: Date, view: View): Date {
  const d = new Date(date);
  if (view === "day") {
    d.setHours(0, 0, 0, 0);
  } else if (view === "week") {
    const day = (d.getDay() + 6) % 7; // Convert Sunday = 0 to Monday = 0
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
  } else if (view === "month") {
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
  } else if (view === "quarter") {
    const quarterStartMonth = Math.floor(d.getMonth() / 3) * 3;
    d.setMonth(quarterStartMonth);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
  } else if (view === "all") {
    // For "all time", return a very early date to capture all data
    return new Date('2020-01-01');
  }
  return d;
}

export function addPeriod(date: Date, view: View, step = 1): Date {
  const d = new Date(date);
  if (view === "day") {
    d.setDate(d.getDate() + step);
  } else if (view === "week") {
    d.setDate(d.getDate() + step * 7);
  } else if (view === "month") {
    d.setMonth(d.getMonth() + step);
  } else if (view === "quarter") {
    d.setMonth(d.getMonth() + step * 3);
  } else if (view === "all") {
    // For "all time", return the same date since navigation doesn't make sense
    return d;
  }
  return d;
}

export function getPeriodNumber(date: Date, view: View): number {
  if (view === "day") {
    // Day of year (1-365/366)
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }
  if (view === "week") {
    // Week number of year (1-53)
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const weekStart = startOfPeriod(date, view);
    const diff = weekStart.getTime() - yearStart.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) + 1;
  }
  if (view === "month") {
    // Month number (1-12)
    return date.getMonth() + 1;
  }
  if (view === "quarter") {
    // Quarter number (1-4)
    return Math.floor(date.getMonth() / 3) + 1;
  }
  // For "all time", return 0 as there's no specific period
  return 0;
}

export function periodLabel(date: Date, view: View, locale?: string): string {
  const periodNumber = getPeriodNumber(date, view);
  const formatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;

  if (view === "day") {
    const dayName = date.toLocaleDateString('en-GB', { weekday: 'long' });
    const dayLabel = date.toLocaleDateString('en-GB', formatOptions);
    return `${dayName} - ${dayLabel} (Day ${periodNumber + 1})`;
  }
  if (view === "week") {
    const s = startOfPeriod(date, "week"); // Ensure we start from Monday
    const e = addPeriod(s, "week", 1);
    e.setDate(e.getDate() - 1); // End on Sunday
    const dateRange = `${s.toLocaleDateString('en-GB', formatOptions)} – ${e.toLocaleDateString('en-GB', formatOptions)}`;
    return `${dateRange} (Week ${periodNumber})`;
  }
  if (view === "month") {
    const monthYear = `${date.toLocaleString('en-GB', { month: "long" })} ${date.getFullYear()}`;
    return `${monthYear} (Month ${periodNumber})`;
  }
  if (view === "quarter") {
    return `Q${periodNumber} ${date.getFullYear()}`;
  }
  // For "all time", return a simple label
  return "All Time";
}

export function withinPeriod(d: Date, start: Date, view: View, customStartDate?: Date | null, customEndDate?: Date | null): boolean {
  if (view === "all") {
    // For "all time" with custom date range, filter by custom dates
    if (customStartDate && customEndDate) {
      // Normalize all dates to compare only year, month, day (ignore time)
      const dateOnly = new Date(d);
      dateOnly.setHours(0, 0, 0, 0);

      const normalizedStart = new Date(customStartDate);
      normalizedStart.setHours(0, 0, 0, 0);

      const normalizedEnd = new Date(customEndDate);
      normalizedEnd.setHours(0, 0, 0, 0);

      // Include any order on start date or end date (entire day)
      return dateOnly >= normalizedStart && dateOnly <= normalizedEnd;
    }
    // For "all time" without custom range, all dates are within the period
    return true;
  }
  const next = addPeriod(start, view, 1);
  return d >= start && d < next;
}

// Helper to calculate end date from start date and view
export function calculateEndDate(startDate: Date, view: View): Date {
  const end = new Date(startDate);

  if (view === 'day') {
    // Same day
    return new Date(startDate);
  } else if (view === 'week') {
    // Start is Monday, end is Sunday (6 days later)
    end.setDate(end.getDate() + 6);
    return end;
  } else if (view === 'month') {
    // Last day of the month
    end.setMonth(end.getMonth() + 1);
    end.setDate(0); // Set to last day of previous month (which is the start month)
    return end;
  } else if (view === 'quarter') {
    // Last day of the quarter
    end.setMonth(end.getMonth() + 3);
    end.setDate(0); // Set to last day of previous month (which is the last month of the quarter)
    return end;
  } else if (view === 'all') {
    // For 'all', return current date as default (will be overridden with actual data range)
    return new Date();
  }

  return end;
}

/**
 * Format a plain calendar date string (YYYY-MM-DD, no timezone) the way the rest
 * of the app renders dates — "3 Sep. 2026". Parsed at local midnight so a UTC
 * read never shifts it a day back.
 */
export function formatPlainDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  return isNaN(parsed.getTime()) ? date : getTableFormattedDate(parsed);
}

export function getTableFormattedDate(date: Date): string {
  return date.toLocaleDateString(undefined, { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  }).replace(/(\d{2}) ([A-Za-z]{3})/, (_, d, m) => `${Number(d)} ${m}.`);
}

/**
 * Translate a whole-day gap into a relative "X days ago" label, using the shared
 * `reports.inventory.relative.*` keys. Single render rule for "how long since",
 * used by the reports hub and the all-restaurants inventory overview.
 */
export function relativeDaysLabel(t: TFunction, daysAgo: number): string {
  if (daysAgo <= 0) return t('reports.inventory.relative.today');
  if (daysAgo === 1) return t('reports.inventory.relative.dayAgo');
  if (daysAgo < 30) return t('reports.inventory.relative.daysAgo', { count: daysAgo });
  const months = Math.floor(daysAgo / 30);
  return months === 1
    ? t('reports.inventory.relative.monthAgo')
    : t('reports.inventory.relative.monthsAgo', { count: months });
}
